/** @prettier */
import React, { useState, useEffect, useCallback } from "react";
import AuditorQuestionList from "./AuditorQuestionsList/AuditorQuestionsList";
import WriterDashboard from "./WriterDashboard/WriterDashboard";
import QuestionEditor from "./Question/QuestionEditor";
import QuestionViewer from "./Question/QuestionViewer";
import HeadBar from "./HeadBar/HeadBar";
import { AppSideBar, StoreSideBar } from "./SideBar/SideBar";
import Login from "./Login/Login";
import Landing from "./Landing/Landing";
import qBankApi, { User, Subscription } from "./services/qbankApi";
import { getInProgressTest } from "./Test/Run/testRunnerApi";
import {
    Route,
    Redirect,
    Switch,
    useRouteMatch,
    useHistory,
} from "react-router-dom";
import HomeDashboard from "./HomeDashboard/HomeDashboard";
import { Registration } from "./Account/Registration";
import StudentDashboard from "./StudentDashboard/StudentDashboard";
import { ResetPassword } from "./Account/PasswordReset";
import CreateTest from "./Test/Create/CreateTest";
import TestList from "./Test/List/TestList";
import TestRunner from "./Test/Run/TestRunner";
import TestReview from "./Test/Review/TestReview";
import TestResults from "./Test/Results/TestResults";
import "./index.css";
import "@fortawesome/fontawesome-free-solid";
import "@fortawesome/fontawesome-free-regular";

type AppState =
    | { authStatus: "loading" }
    | { authStatus: "unauthenticated"; message?: string }
    | {
          authStatus: "authenticated";
          activeSubscription: Subscription | undefined;
          me: User;
      };

const App = () => {
    const history = useHistory();
    const [authState, setAuthState] = useState<AppState>({
        authStatus: "loading",
    });
    const setActiveSubscription = useCallback(
        // takes `me` param to avoid extracting it out of authState
        async (me: User, subscription: Subscription) => {
            await qBankApi.changeActiveProduct({
                userUuid: me.uuid,
                productUuid: subscription.product.uuid,
            });
            setAuthState((authState) => ({
                ...authState,
                activeSubscription: subscription,
            }));
        },
        [],
    );
    useEffect(() => {
        qBankApi
            .getMe()
            .then((me) => {
                setAuthState({
                    authStatus: "authenticated",
                    me,
                    activeSubscription:
                        me.subscriptions.find(
                            ({ product }) =>
                                product.uuid === me.lastUsedProductUuid,
                            // Fall-back on the first subscription, in the unlikely case
                            //   that we don't find the last used product.
                        ) ?? me.subscriptions?.[0],
                });
            })
            .catch(() => {
                setAuthState({ authStatus: "unauthenticated" });
            });
    }, []);

    // Try to navigate to an in-progress test (if logged in as a student)
    useEffect(() => {
        if (
            authState.authStatus !== "authenticated" ||
            !authState.activeSubscription?.student
        ) {
            return;
        }
        getInProgressTest().then((maybeUuid) => {
            if (maybeUuid) {
                history.push(`/tests/${maybeUuid}/run`);
            }
        });
    }, [authState, history]);

    const onLogout = () => {
        setAuthState({
            authStatus: "unauthenticated",
            message: "Your session has expired, please login again.",
        });
        history.push("/");
    };

    // Sidebar is hidden when taking a test
    const isTakingTest = !!useRouteMatch("/tests/:testId/run");

    const getParam = (param: string, location: any): string => {
        const params = new URLSearchParams(location.search);
        return params.get(param) ?? "";
    };

    if (authState.authStatus === "loading") {
        return <div></div>;
    } else if (authState.authStatus === "unauthenticated") {
        return (
            <Switch>
                <Route
                    path="/register"
                    render={({ location }) => (
                        <Registration
                            withPassword={false}
                            token={getParam("token", location)}
                        />
                    )}
                />
                <Route
                    path="/userRegistration"
                    render={({ location }) => (
                        <Registration
                            withPassword={true}
                            token={getParam("token", location)}
                        />
                    )}
                />
                <Route exact path="/login">
                    <Login message={authState.message} />
                </Route>
                <Route
                    path="/resetPassword"
                    render={({ location }) => (
                        <ResetPassword
                            resetToken={getParam("token", location)}
                            key={getParam("token", location)}
                        />
                    )}
                />
                <Route>
                    <Landing message={authState.message} />
                </Route>
            </Switch>
        );
    }
    const { activeSubscription, me } = authState;

    return (
        <div className="app-container">
            <HeadBar
                me={me}
                activeSubscription={activeSubscription}
                setActiveSubscription={setActiveSubscription}
            />
            <div className="app-row-container">
                <Switch>
                    <Route path="/store">
                        <StoreSideBar />
                    </Route>
                    <Route path="/">
                        {!isTakingTest && (
                            <AppSideBar activeSubscription={activeSubscription} />
                        )}
                    </Route>
                </Switch>
                <div className="app-column-container">
                    <CenterStage
                        /*
                            Force the center stage to rerender when the selected product changes.
                            This ensures that all UIs (e.g. tables) reset to reflect the new product selection
                        */
                        key={activeSubscription?.product.uuid}
                        {...authState}
                        onLogout={onLogout}
                    />
                </div>
            </div>
        </div>
    );
};

const noop = () => {
    /* do nothing */
};

type CenterStageProps = Extract<AppState, { authStatus: "authenticated" }> & {
    onLogout: () => void;
};
const CenterStage = ({
    activeSubscription,
    me,
    onLogout,
}: CenterStageProps) => {
    // Setup the logout callback here,
    //  so we only trigger it when we were actually logged in
    useEffect(() => {
        qBankApi.setLogoutCallback(onLogout);
        return () => qBankApi.setLogoutCallback(noop);
    });
    return (
        <Switch>
            <Route path="/store">
                <></>
            </Route>
            <Route exact path="/dashboard">
                {activeSubscription && activeSubscription.student ? (
                    <StudentDashboard
                        activeSubscription={activeSubscription}
                        me={me}
                    />
                ) : (
                    <HomeDashboard />
                )}
            </Route>
            {activeSubscription && [
                <Route exact path="/tests">
                    <TestList
                        userId={me.uuid}
                        activeSubscription={activeSubscription}
                    />
                </Route>,
                <Route exact path="/tests/create">
                    <CreateTest activeSubscription={activeSubscription} />
                </Route>,
                <Route
                    exact
                    path="/tests/:testId/run"
                    render={({ match }) => (
                        <TestRunner id={match.params.testId} />
                    )}
                />,
                <Route
                    exact
                    path="/tests/:testId/review"
                    render={({ match, location }) => {
                        const params = new URLSearchParams(location.search);
                        const filter =
                            // pushState state
                            location.state?.filter ??
                            // ?filter param
                            params.get("filter") ??
                            undefined;
                        const numberParam = parseInt(
                            params.get("number") ?? "",
                        );
                        const number = !isNaN(numberParam)
                            ? numberParam
                            : undefined;
                        return (
                            <TestReview
                                id={match.params.testId}
                                filter={filter}
                                number={number}
                            />
                        );
                    }}
                />,
                <Route
                    exact
                    path="/tests/:testId/results"
                    render={({ match }) => (
                        <TestResults id={match.params.testId} />
                    )}
                />,
                <Route exact path="/questions">
                    <WriterDashboard
                        me={me}
                        activeSubscription={activeSubscription}
                    />
                </Route>,
                <Route exact path="/audits">
                    <AuditorQuestionList
                        me={me}
                        activeSubscription={activeSubscription}
                    />
                </Route>,
                <Route path="/questions/:questionId/view">
                    <QuestionViewer
                        me={me}
                        activeSubscription={activeSubscription}
                    />
                </Route>,
                <Route path="/questions/:questionId/edit">
                    <QuestionEditor
                        me={me}
                        activeSubscription={activeSubscription}
                        qBankApi={qBankApi}
                    />
                </Route>,
            ]}
            <Route>
                <Redirect to="/dashboard" />
            </Route>
        </Switch>
    );
};

export default App;
